import { isInBrowser } from './browser'

import { Stripe } from '@stripe/stripe-js'
import { navigate } from 'gatsby'
import type { AccountAuthUser } from '../async/account'
import {
  cancelAccountVerification,
  getAccount,
  getPublicProfileActivity,
  listFollowing,
  loadAccountSnapshot,
  mapPublicProfileCardToLegacyUser,
  removeAction,
  saveAction,
  takeAction,
  unsaveAction,
  updateAppPreferences,
  updateProfile,
  updatePrivacySettings,
  updateProgression,
  waitForAccountInitialization
} from '../async/account'
import { getUserFromEmail, updateSubscriberMetadata } from '../async/beehiiv'
import { fetchActions, fetchActionsForProfile } from '../async/sanity'
import {
  cancelTest,
  checkVerification,
  createStripeVerificationSession,
  testVerification
} from '../async/stripe'
import { trackSupabaseEvent } from '../async/supabase'
import { attempt } from '../async/utils'
import { HEADER_ONBOARDING_STEPS } from '../components/headers/header-onboarding/header-onboarding'
import { GIVE_LIVELY_DONATION_ACTION_SUFFIX } from '../constants/actions'
import { SocialHref } from '../constants/socials'
import {
  ActionToolAccountWithId,
  ActionToolUserWithId,
  DateValueLike,
  UserActionHistory,
  UserFriendFeedData,
  UserPrivacySettings,
  UserProfile
} from '../types/account-types'
import { UserActionHistoryData } from '../types/component-types'
import { GiveLivelyDonationConfig } from '../types/give-lively-types'
import { OnboardingStep, OnboardingStepToUrl } from '../types/onboarding-types'
import { trackSegmentIdentity } from './analytics'
import { arrayHasLength } from './array'
import { updateLsArray } from './ls'

export interface PartialUserActionHistory {
  action_slug: string
  user_action_id?: string
  action_id?: string
  date_completed?: Date
  meta_data?: object
}

type LegacyAccountUpdateMap = Record<string, unknown | null>

const createLegacyTimestamp = (value: string) =>
  ({
    toDate: () => new Date(value)
  }) as DateValueLike

export const getFavorites = (account?: ActionToolAccountWithId) => {
  let favorites: string[] = account?.user?.saved_actions || []
  if (!favorites && isInBrowser()) {
    // if user is logged in, use their favorites
    const lsFavorites = window.localStorage.getItem('INI_AT__USER_FAVORITES')
    favorites = lsFavorites?.split(',') || []
  }
  return favorites
}

export const toggleFavorite = async (
  slug: string,
  favorited: boolean,
  account?: ActionToolAccountWithId
) => {
  return await attempt(async () => {
    const favorites = [...getFavorites(account)]

    const currentFavorited = favorites.indexOf(slug) > -1
    if (!favorited && currentFavorited) {
      // remove from favorites
      favorites.splice(favorites.indexOf(slug), 1)
    } else if (favorited && !currentFavorited) {
      // add to favorites
      favorites.push(slug)
    }

    if (account?.id) {
      if (favorited && !currentFavorited) {
        await saveAction(account.id, slug)
      } else if (!favorited && currentFavorited) {
        await unsaveAction(account.id, slug)
      }
    } else if (isInBrowser()) {
      updateLsArray('INI_AT__USER_FAVORITES', favorites)
    }
    return favorites
  })
}

export const getSocialUrl = (href?: string, socialHref?: SocialHref) => {
  if (href && socialHref) {
    try {
      const url = new URL(href)
      if (url.origin == socialHref.baseUrl) {
        return url.href
      }
    } catch {
      return `${socialHref.profileUrl}${href}`
    }
  }
}

export const getActionsTakenIdsByDateDesc = async (actionsTaken?: UserActionHistory[]) => {
  if (!actionsTaken || actionsTaken.length === 0) {
    return
  }

  return actionsTaken.map(({ action_slug }) => action_slug)
}

export const getUserActionData = async (actionsTaken: UserActionHistory[]) => {
  const actionSlugs = actionsTaken.map(({ action_slug }) => action_slug)
  // One entry per slug, in order: removed actions come back as "no longer
  // available" snapshots instead of leaving a gap.
  const actions = await fetchActionsForProfile(actionSlugs)

  return actionsTaken
    .map((actionTaken, index) => {
      const data = actions[index]
      if (!data) {
        return null
      }

      return {
        ...data,
        action_id: actionTaken?.action_id,
        user_action_id: actionTaken?.user_action_id,
        date_completed: actionTaken.date_completed,
        meta_data: actionTaken.meta_data
      } as UserActionHistoryData
    })
    .filter((action): action is UserActionHistoryData => Boolean(action))
    .sort((a, b) => (b.date_completed?.getTime() ?? 0) - (a.date_completed?.getTime() ?? 0))
}

export const getActionsTakenByUsers = async (
  ids: string[],
  sourceAccountId?: string
): Promise<UserFriendFeedData> => {
  void ids

  if (!sourceAccountId) {
    return []
  }

  const following = await listFollowing(sourceAccountId, sourceAccountId)
  if (following.length === 0) {
    return []
  }

  const followingActivity = await Promise.all(
    following.map(async profile => ({
      activity: await getPublicProfileActivity(profile.handle, sourceAccountId),
      profile
    }))
  )

  const actionSlugs = Array.from(
    new Set(
      followingActivity.flatMap(({ activity }) =>
        (activity?.actionHistory ?? []).map(action => action.actionSlug).filter(Boolean)
      )
    )
  ) as string[]
  const actionData = actionSlugs.length > 0 ? await fetchActions(actionSlugs) : []

  return followingActivity
    .flatMap(({ activity, profile }) => {
      if (!activity || activity.hideHistory) {
        return []
      }

      const compatUser = mapPublicProfileCardToLegacyUser(profile)
      const compatAccount: ActionToolAccountWithId = {
        id: profile.id,
        unique_id: profile.id,
        user_id: profile.user_id,
        email: '',
        premium_access: false,
        beehiiv_id: null,
        stripe_customer_id: null,
        stripe_subscription_id: null,
        stripe_verification: null,
        user_progression: {},
        user: compatUser
      }

      return activity.actionHistory.map(actionTaken => ({
        action: actionData?.find(({ slug }) => slug?.current === actionTaken.actionSlug),
        action_id: actionTaken.actionId ?? undefined,
        action_slug: actionTaken.actionSlug,
        date_completed: actionTaken.completedAt ? new Date(actionTaken.completedAt) : undefined,
        profile: compatAccount,
        user_action_id: actionTaken.userActionId ?? undefined,
        user_id: profile.id
      }))
    })
    .sort((a, b) => (b.date_completed?.getTime() ?? 0) - (a.date_completed?.getTime() ?? 0))
}

export const getActionHistory = (account?: ActionToolAccountWithId) => {
  return account?.user?.action_history || []
}

export const setVerificationModalShown = async (uid: string, shown: boolean) => {
  await updateProgression(uid, {
    verificationPromptDismissedAt: shown ? new Date().toISOString() : null
  })
}

export const applyVerificationModalShown = (
  account: ActionToolAccountWithId | undefined,
  shownAt: string
) => {
  if (!account) {
    return account
  }

  return {
    ...account,
    stripe_verification: {
      session_id: account.stripe_verification?.session_id ?? null,
      status: account.stripe_verification?.status ?? 'unverified',
      public_intent: account.stripe_verification?.public_intent,
      modal_date: createLegacyTimestamp(shownAt),
      verified_fields: account.stripe_verification?.verified_fields ?? null
    }
  }
}

export const toggleActionTaken = async (
  slug: string,
  taken: boolean,
  account?: ActionToolAccountWithId,
  options?: {
    actionId?: string
  }
) => {
  return await attempt(async () => {
    const actionHistory: UserActionHistory | PartialUserActionHistory[] = [
      ...getActionHistory(account)
    ]
    const actionCurrentlyTaken = actionHistory.find(({ action_slug, action_id }) =>
      options?.actionId
        ? action_slug === slug && action_id === options.actionId
        : action_slug === slug
    )
    const uid = account?.id

    if (taken && !actionCurrentlyTaken) {
      if (uid) {
        const result = await takeAction(uid, {
          actionSlug: slug,
          actionId: options?.actionId ?? null,
          completedAt: new Date().toISOString()
        })
        actionHistory.push({
          action_slug: result.value.actionSlug,
          action_id: result.value.actionId ?? undefined,
          user_action_id: result.value.id,
          date_completed: result.value.completedAt ? new Date(result.value.completedAt) : undefined
        })
      } else {
        actionHistory.push({
          action_slug: slug
        })
      }
    } else if (!taken && actionCurrentlyTaken) {
      const actionIndex = actionHistory.findIndex(({ action_slug, action_id }) =>
        options?.actionId
          ? action_slug === slug && action_id === options.actionId
          : action_slug === slug
      )
      const [removed] = actionHistory.splice(actionIndex, 1)

      if (uid) {
        await removeAction(uid, {
          actionSlug: slug,
          actionId: removed?.action_id ?? options?.actionId ?? null
        })
      }
    }

    if (uid) {
      /*
      // Previous Firebase event-bus write:
      // URL came from the WCID_EVENTS_URL env var, which pointed at the
      // wcid-events Firebase Function /eventHandler endpoint.
      // Example production shape:
      // https://us-central1-wcid-events-8179f.cloudfunctions.net/eventHandler
      await fetch(process.env.WCID_EVENTS_URL || '<WCID_EVENTS_URL>/eventHandler', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-wcid-secret': process.env.WCID_EVENTS_SECRET || '<WCID_EVENTS_SECRET>'
        },
        body: JSON.stringify({
          type: taken ? 'action_taken' : 'action_removed',
          userId: uid,
          metadata: {
            action_id: slug
          }
        })
      })
      */

      void trackSupabaseEvent({
        eventType: taken ? 'action_taken' : 'action_removed',
        userId: uid,
        actionSlug: slug,
        occurredAt: new Date().toISOString(),
        metadata: {
          source: 'actiontoolwebapp'
        }
      }).catch(error => {
        console.warn('Supabase track_event failed', error)
      })
    }

    return actionHistory
  })
}

export const createGiveLivelyDonationAction = async (
  account: ActionToolAccountWithId,
  action_id: string,
  config: GiveLivelyDonationConfig
) => {
  const { amount, isPublic, isRecurring } = config
  return await attempt(async () => {
    const actionHistory: UserActionHistory | PartialUserActionHistory[] = [
      ...getActionHistory(account)
    ]

    const title = [
      'Made a',
      isPublic && `$${amount}${isRecurring ? ' recurring' : ''}`,
      'donation with Give Lively'
    ]
      .filter(exists => !!exists)
      .join(' ')

    const data = {
      actionSlug: action_id,
      actionId: action_id + GIVE_LIVELY_DONATION_ACTION_SUFFIX,
      completedAt: new Date().toISOString(),
      title,
      externalUrl: config.url,
      metaType: 'give-lively-donation'
    }

    const result = await takeAction(account.id, data)

    actionHistory.push({
      action_slug: result.value.actionSlug,
      action_id: result.value.actionId ?? undefined,
      user_action_id: result.value.id,
      date_completed: result.value.completedAt ? new Date(result.value.completedAt) : undefined,
      meta_data: {
        title: result.value.title ?? undefined,
        external_url: result.value.externalUrl ?? undefined,
        meta_type: result.value.metaType ?? undefined,
        type: result.value.metaType === 'give-lively-donation' ? 'donation' : undefined
      }
    })

    return actionHistory
  })
}

export const initializeAccount = async (
  accountId: string,
  email: string,
  onLoadedAccount?: (user: ActionToolAccountWithId) => void
) => {
  const existingAccount = await waitForAccountInitialization(accountId)

  if (!existingAccount) {
    return await loadAccountSnapshot(accountId, onLoadedAccount)
  }

  onLoadedAccount?.(existingAccount)

  trackSegmentIdentity(accountId, {
    email,
    nouns: existingAccount.user?.profile?.private?.nouns,
    verbs: existingAccount.user?.profile?.private?.verbs,
    cobenefits: existingAccount.user?.profile?.private?.cobenefits,
    skills: existingAccount.user?.profile?.private?.skills,
    country: existingAccount.user?.profile?.private?.location_data?.country,
    state: existingAccount.user?.profile?.private?.location_data?.state,
    city: existingAccount.user?.profile?.private?.location_data?.city
  })

  return existingAccount
}

const VERIFICATION_CHECK_ATTEMPTS = 5
const VERIFICATION_CHECK_INTERVAL_MS = 2000

// A Stripe Identity client secret is "<session id>_secret_<token>".
const getVerificationSessionId = (clientSecret: string) => clientSecret.split('_secret_')[0]

// Asks Stripe for the session's result as soon as the modal closes, rather than
// waiting for the verification webhook, and records it. Stripe can still be
// processing the documents at that point, so check again a few times.
const confirmVerificationResult = async (sessionId: string) => {
  for (let check = 1; check <= VERIFICATION_CHECK_ATTEMPTS; check++) {
    const result = await checkVerification(sessionId)
    if (result?.status !== 'processing' || check === VERIFICATION_CHECK_ATTEMPTS) {
      return result?.status
    }

    await new Promise(resolve => setTimeout(resolve, VERIFICATION_CHECK_INTERVAL_MS))
  }
}

const useStripeVerification = true
// Resolves with the reloaded account once the result is recorded, so callers can
// update account state before moving on (the verification gate reads it).
export const initializeUserVerification = async (
  client: Stripe,
  authUser: AccountAuthUser,
  publicIntent: boolean,
  visibleToEveryone: boolean
) => {
  await updateProgression(authUser.uid, {
    verificationPublicIntent: Boolean(publicIntent)
  })

  // Unlike public intent this is an ordinary privacy setting, so it can be saved
  // now. It only matters for a viewable profile, so leave it alone otherwise.
  if (publicIntent) {
    await updatePrivacySettings(authUser.uid, {
      hideFromAnonymous: !visibleToEveryone
    })
  }

  if (useStripeVerification) {
    let refreshedAccount: ActionToolAccountWithId | undefined

    await attempt(async () => {
      const clientSecret = await createStripeVerificationSession(authUser.uid)

      if (clientSecret) {
        const session = await client.verifyIdentity(clientSecret)
        const { error } = session
        if (error) {
          throw new Error(
            `We weren't able to complete your verification attempt; please try again.`
          )
        }

        await confirmVerificationResult(getVerificationSessionId(clientSecret))
        refreshedAccount = (await getAccount(authUser.uid)) || undefined
      } else {
        throw new Error('Unable to create Stripe verification session')
      }
    })

    return refreshedAccount
  } else {
    if (authUser.email) {
      await testVerification(authUser.email, 'Conor Britain')
    } else {
      throw new Error('No email for authUser found!')
    }
  }
}

export const checkUserVerification = async (session_id: string) => {
  await checkVerification(session_id)
}

// What other people see of a profile: the user with their hidden fields
// removed. Returns a copy and never modifies the user passed in (it used to
// delete fields in place, which stripped the owner's own account state when
// previewing their profile).
export const stripPrivateData = (doc: ActionToolUserWithId) => {
  if (doc.settings?.is_private) return
  const privacy = doc.settings?.privacy
  const privateProfile = { ...doc.profile.private }

  if (privacy?.hide_location == true) {
    delete privateProfile.location
    delete privateProfile.location_data
  }
  if (privacy?.hide_pronouns == true) {
    delete privateProfile.pronouns
  }
  if (privacy?.hide_skills == true) {
    delete privateProfile.skills
  }
  if (privacy?.hide_verbs == true) {
    delete privateProfile.verbs
  }
  if (privacy?.hide_nouns == true) {
    delete privateProfile.nouns
  }
  if (privacy?.hide_cobenefits == true) {
    delete privateProfile.cobenefits
  }

  const stripped: ActionToolUserWithId = {
    ...doc,
    profile: { ...doc.profile, private: privateProfile }
  }
  if (privacy?.hide_history == true) {
    delete stripped.action_history
  }
  return stripped
}

// Resolves with the reloaded account so callers can show the cancelled state.
export const removeUserVerfication = async (account?: ActionToolAccountWithId) => {
  if (useStripeVerification) {
    if (!account?.id) {
      throw new Error('No account found')
    }
    return (await cancelAccountVerification(account.id)) || undefined
  } else {
    if (account?.email) {
      await cancelTest(account.email)
    } else {
      throw new Error('No account email found')
    }
  }
}

export const getAccountOnboardingStatus = (account?: ActionToolAccountWithId): OnboardingStep => {
  const onboardingStepStr = account?.user_progression?.onboarding_latest_step_reached
  const onboardingStep = Object.values(OnboardingStep).includes(onboardingStepStr as OnboardingStep)
    ? (onboardingStepStr as OnboardingStep)
    : null
  return onboardingStep ? onboardingStep : OnboardingStep.NOT_STARTED
}

const syncOnboardingUpdatesToCore = async (
  accountId: string,
  reachedStep?: OnboardingStep,
  updates?: LegacyAccountUpdateMap
) => {
  const profileInput: Parameters<typeof updateProfile>[1] = {}
  const progressionInput: Parameters<typeof updateProgression>[1] = {}

  if (reachedStep) {
    progressionInput.onboardingLatestStepReached = reachedStep
  }

  for (const [key, value] of Object.entries(updates || {})) {
    switch (key) {
      case 'user.profile.private.verbs':
        profileInput.verbs = Array.isArray(value) ? (value as string[]) : []
        break
      case 'user.profile.private.nouns':
        profileInput.nouns = Array.isArray(value) ? (value as string[]) : []
        break
      case 'user.profile.private.skills':
        profileInput.skills = Array.isArray(value) ? (value as string[]) : []
        break
      case 'user.profile.private.cobenefits':
      case 'user.profile.private.co-benefits':
        profileInput.cobenefits = Array.isArray(value) ? (value as string[]) : []
        break
      case 'user_progression.onboarding_latest_step_reached':
        progressionInput.onboardingLatestStepReached =
          typeof value === 'string' ? value : (progressionInput.onboardingLatestStepReached ?? null)
        break
      case 'user_progression.should_subscribe':
        progressionInput.shouldSubscribe = typeof value === 'boolean' ? value : null
        break
      default:
        break
    }
  }

  if (Object.keys(profileInput).length > 0) {
    await updateProfile(accountId, profileInput)
  }

  if (Object.keys(progressionInput).length > 0) {
    await updateProgression(accountId, progressionInput)
  }
}

export const updateAccountAndOnboardingStatus = async (
  reachedStep: OnboardingStep,
  account: ActionToolAccountWithId,
  updates?: LegacyAccountUpdateMap
) => {
  if (account == null) {
    return
  }

  if (account.user_progression?.onboarding_latest_step_reached) {
    const latestStepNumberReached = HEADER_ONBOARDING_STEPS.findIndex(
      step => step === account?.user_progression?.onboarding_latest_step_reached
    )
    const reachedStepNumber = HEADER_ONBOARDING_STEPS.findIndex(step => step === reachedStep)
    if (reachedStep !== OnboardingStep.COMPLETED && latestStepNumberReached >= reachedStepNumber) {
      await syncOnboardingUpdatesToCore(account.id, undefined, updates)
      return
    }
  }

  await syncOnboardingUpdatesToCore(account.id, reachedStep, updates)
}

export const updateSubscriptionIntent = async (
  subscribe: boolean,
  account: ActionToolAccountWithId
) => {
  if (account == null) {
    return
  }

  await updateProgression(account.id, {
    shouldSubscribe: subscribe
  })
}

export const syncSubscriptionWithBeehiiv = async (accountId: string, shouldSubscribe: boolean) => {
  const account = await getAccount(accountId)
  if (account) {
    const existing = await getUserFromEmail(account.email)
    if (existing) {
      try {
        await updateSubscriberMetadata(existing.id, {
          subscribe: shouldSubscribe
        })
        await updateAppPreferences(account.id, {
          subscribedToIni: shouldSubscribe
        })
      } catch {}
    }
  }
}

export const syncSubscriptionIntentWithBeehiiv = async (accountId: string) => {
  const account = await getAccount(accountId)
  if (account) {
    if (account.user_progression.should_subscribe !== undefined) {
      await syncSubscriptionWithBeehiiv(accountId, account.user_progression.should_subscribe)
      await updateProgression(account.id, {
        shouldSubscribe: null
      })
    }
  }
}

export const navigateToProfile = (account?: ActionToolAccountWithId) => {
  profileAccessRedirectHandler(account, () =>
    navigate(account?.user_id ? `/profile/${account?.user_id}` : '/profile')
  )
}

export const profileAccessRedirectHandler = (
  account?: ActionToolAccountWithId,
  onProfileAccessAllowed?: () => void
) => {
  if (account == null) {
    navigate('/register')
    return
  }

  const onboardingStatus = getAccountOnboardingStatus(account)
  if (onboardingStatus !== OnboardingStep.COMPLETED) {
    navigate(OnboardingStepToUrl[onboardingStatus])
  } else if (!account.premium_access) {
    navigate('/register/upgrade')
  } else if (onProfileAccessAllowed) {
    onProfileAccessAllowed()
  }
}

export const getPublicLocationFromProfile = (profile?: UserProfile) => {
  const locationPieces = [
    profile?.private?.location_data?.city,
    profile?.private?.location_data?.state
    // profile?.private?.location_data?.country
  ]
  return (
    locationPieces &&
    locationPieces.length > 0 &&
    locationPieces.filter(piece => !!piece).join(', ')
  )
}

export const privacySettingIsOff = (
  key: keyof UserPrivacySettings,
  user?: ActionToolUserWithId
) => {
  return user?.settings?.privacy && !user.settings.privacy[key]
}

export const hasProfileDetails = (user?: ActionToolUserWithId) => {
  const location = getPublicLocationFromProfile(user.profile)
  const occupation = user?.profile?.private?.occupation
  const skills =
    privacySettingIsOff('hide_skills', user) && arrayHasLength(user?.profile?.private?.skills)

  const verbs =
    privacySettingIsOff('hide_verbs', user) && arrayHasLength(user?.profile?.private?.verbs)
  const nouns =
    privacySettingIsOff('hide_nouns', user) && arrayHasLength(user?.profile?.private?.nouns)
  const cobenefits =
    privacySettingIsOff('hide_cobenefits', user) &&
    arrayHasLength(user?.profile?.private?.cobenefits)

  return location || occupation || skills || verbs || nouns || cobenefits
}

export const showProfileActionData = (user?: ActionToolUserWithId) => {
  const canViewTodos = privacySettingIsOff('hide_todo', user)
  const canViewHistory = privacySettingIsOff('hide_history', user)

  return canViewTodos || canViewHistory
}
